import { ExploreWidgetConfig } from "../config"
import { ELEVATED_TOKEN_SCOPE } from "./elevatedScope"

/**
 * Searchbar Pro dynamic tokens (#853). Pure builders (same pattern as
 * buildMapLayoutCss) so the derivations are unit-testable.
 *
 * Accent derivation is a faithful port of the toolkit's server-side math
 * (Shortcode::resolve_accent): hover = each RGB channel ×0.88 rounded,
 * focus ring = the accent at 25% alpha. Computing (not hand-picking) the
 * hover shade keeps brand recoloring consistent with the reference at any
 * accent hue.
 *
 * #1844: `searchbar.accentColor` has NO literal default any more. Unset means
 * "the theme accent": the token block points `--hfse-sbp-accent` at
 * `var(--hfse-accent)` — which useExplorerCss already dual-emits for light,
 * dark and the auto first paint on every element the block scopes — and
 * derives hover / ring with color-mix() (the same ×0.88 / 25% math, evaluated
 * by the browser so it tracks whichever palette is live). The toolkit's own
 * default is #ff385c (Airbnb red): a fresh explorer on a blue-themed site
 * painted a red search button (Foothills GAW, feed 416).
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex.trim())
  if (!m) return null
  let h = m[1]
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

export function deriveAccent(hex: string): { accent: string; hover: string; ring: string } | null {
  // The toolkit's resolve_accent() substitutes #ff385c for unparseable input.
  // Here that literal is exactly what #1844 removed, so unparseable returns
  // null and accentTokens() takes the inherit path — "garbage" and "unset"
  // both resolve to the theme accent, and no caller can get Airbnb red back.
  const rgb = hexToRgb(hex)
  if (!rgb) return null
  const to2 = (n: number) => Math.round(n).toString(16).padStart(2, '0')
  const accent = `#${to2(rgb.r)}${to2(rgb.g)}${to2(rgb.b)}`
  const hover = `#${to2(rgb.r * 0.88)}${to2(rgb.g * 0.88)}${to2(rgb.b * 0.88)}`
  const ring = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`
  return { accent, hover, ring }
}

/** Master corner clamp (toolkit: one token clamps every radius; circles exempt). */
export function shapeMax(shape: 'pill' | 'rounded' | 'square'): string {
  if (shape === 'rounded') return '12px'
  if (shape === 'square') return '0px'
  return '999px'
}

const SCRIM_COLORS: Record<'white' | 'dark', string> = {
  white: 'rgba(255, 255, 255, 0.96)',
  dark: 'rgba(17, 24, 39, 0.92)',
}

/**
 * Every surface the SBP tokens must reach — one list, so the accent fallback
 * block in buildSearchbarCss can never drift from the token block.
 *
 * The mobile sheet is portaled into the elevated dialog shadow root, OUTSIDE
 * .hfse-sbp's variable scope — the tokens are mirrored onto its selector
 * (toolkit parity: SBP manually mirrors accent/shape onto its portaled modal).
 * `.hfse-sbp-popover` is the same trap one mount point over: the date, guests,
 * additional-filters AND What dialogs carry it and are portaled the same way,
 * so without it every token below falls back inside those popovers — the flex
 * chips rendered the #ff385c default while the identical chip under the bar
 * (inside .hfse-sbp) took the configured accent, `shape` reached neither, and
 * the What popover's price-histogram bars would render un-branded (it also
 * carries .hfse-sbp-what, but the shared class is the one mirrored here).
 * Sizing tokens ride the SAME shared selector as the color tokens, and for the
 * same reason: the mobile sheet re-renders every field (label + value) inside
 * its own portaled tree, so typography emitted only on .hfse-sbp would leave
 * the sheet on the stylesheet fallbacks. --hfse-sbp-max-width is inert on the
 * sheet (the desktop bar is display:none there) but is kept on one selector
 * rather than split across two, so the token block stays a single source.
 * ELEVATED_TOKEN_SCOPE (#1259): the sticky shells pin through the widget-core
 * elevated body portal (hero #951, refine #1259), whose re-parented container
 * carries the layout-free scope class and has no .hfse-sbp ancestor. Without
 * it here the pinned bar dropped to the stylesheet fallbacks the moment it
 * left the widget tree (hostile bench: accent #4a90e2 -> #ff385c, shape 12px
 * -> 999px), and positionRefine's computed read of --hfse-sbp-max-width on a
 * resize while pinned would clamp against the 850 fallback too.
 */
export const SBP_TOKEN_SCOPE = `.hfse-sbp, .hr-dialog-content.hfse-sbp-mobile-sheet, .hr-dialog-content.hfse-sbp-popover, .${ELEVATED_TOKEN_SCOPE}`

/**
 * The three accent tokens of the token block (#1844): the toolkit hex trio for
 * an operator-set accent, or the theme-following trio when
 * `searchbar.accentColor` is unset. `color-mix(in srgb, A 88%, #000)` is
 * exactly 0.88 × each channel and `A 25%, transparent` is A at alpha 0.25 —
 * the toolkit math, evaluated by the browser against whichever --hfse-accent
 * is live (light, dark, auto first paint). Every element SBP_TOKEN_SCOPE names
 * either carries --hfse-accent itself (.hr-dialog-content, the elevated scope)
 * or sits inside .hfse-explorer-container, which does.
 */
export function accentTokens(accentColor: string | undefined): string {
  const derived = accentColor ? deriveAccent(accentColor) : null
  if (derived) {
    return `--hfse-sbp-accent: ${derived.accent};
      --hfse-sbp-accent-hover: ${derived.hover};
      --hfse-sbp-accent-ring: ${derived.ring};`
  }
  return `--hfse-sbp-accent: var(--hfse-accent);
      --hfse-sbp-accent-hover: color-mix(in srgb, var(--hfse-accent) 88%, #000);
      --hfse-sbp-accent-ring: color-mix(in srgb, var(--hfse-accent) 25%, transparent);`
}

export function buildSearchbarCss(options: Pick<ExploreWidgetConfig, 'searchbar' | 'stickyFilter'>): string {
  const sb = options.searchbar
  if (!sb || sb.variant !== 'pro') return ''
  const parts: string[] = [`
    ${SBP_TOKEN_SCOPE} {
      ${accentTokens(sb.accentColor)}
      --hfse-sbp-shape-max: ${shapeMax(sb.shape)};
      --hfse-sbp-max-width: ${sb.maxWidthPx}px;
      --hfse-sbp-label-size: ${sb.labelSizePx}px;
      --hfse-sbp-input-size: ${sb.inputSizePx}px;
    }`]

  if (!(sb.accentColor && deriveAccent(sb.accentColor))) {
    // A var() that resolves to an unsupported function makes the consuming
    // property `unset`, not "the stylesheet fallback": on an engine without
    // color-mix() (Safari ≤ 16.1, Chrome ≤ 110) the search button — the
    // primary CTA — would go transparent on hover and the focus ring would
    // vanish. The stylesheet's other color-mix() uses degrade softly there
    // (a chip tint declared directly is dropped at parse time; the datepicker
    // range tint goes unset), which is why only these two tokens get a guard.
    parts.push(`
    @supports not (color: color-mix(in srgb, #000 50%, #fff)) {
      ${SBP_TOKEN_SCOPE} {
        --hfse-sbp-accent-hover: var(--hfse-accent);
        --hfse-sbp-accent-ring: var(--hfse-accent);
      }
    }`)
  }

  // Scrim renders only in the sticky-filter configuration (toolkit parity: the
  // scrim exists for grid-scroll contrast under a pinned bar, never the hero).
  // #1092: it lives on the PINNED refine shell (module css:3764-3795) — a
  // z-index:-1 pseudo behind the transparent fixed shell, fading over the
  // content scrolling under the bar. The old placement (.hfse-sbp under the
  // legacy container pin) put the gradient on the element that ALSO carried
  // the pin's opaque theme background, so it painted "white fading to grey"
  // and stopped at the block's hard bottom edge — Eli's observed symptom on
  // the GAW demo /properties. The -12px top mirrors the shell's 12px pin
  // offset, so the strip reaches the viewport top (or the bottom of a fixed
  // site header when search.heroStickyTopPx is set).
  if (options.stickyFilter && sb.scrim.style !== 'none') {
    parts.push(`
    .hfse-sbp-refine-shell-sticky::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: -12px;
      height: calc(100% + 12px + ${sb.scrim.heightPx}px);
      z-index: -1;
      pointer-events: none;
      background: linear-gradient(to bottom, ${SCRIM_COLORS[sb.scrim.style]}, transparent);
      -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 44%, transparent 100%);
      mask-image: linear-gradient(to bottom, #000 0%, #000 44%, transparent 100%);
      ${sb.scrim.blur ? '-webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);' : ''}
    }`)
  }

  return parts.join('\n')
}
